import { useEffect, useMemo, useRef } from "react";
import { createPortal } from "react-dom";
import { X, Check } from "lucide-react";
import PropTypes from "prop-types";
import { useAppContext } from "../../../context/AppContext";
import useLocalization from "../../../hooks/useLocalization";
import "./LanguageModal.css";
import styled from "styled-components";

// mapping language codes to image assets (keep after imports to satisfy ESLint)
const ICONS = {
  en: require("../../../assets/images/English.png"),
  as: require("../../../assets/images/assam.png"),
  bn: require("../../../assets/images/bengala.png"),
  brx: require("../../../assets/images/bodo.png"),
  doi: require("../../../assets/images/dongari.png"),
  gom: require("../../../assets/images/konkani.png"),
  gu: require("../../../assets/images/gujarati.png"),
  hi: require("../../../assets/images/hindi.png"),
  kn: require("../../../assets/images/kannad.png"),
  ks: require("../../../assets/images/kashmiri.png"),
  mai: require("../../../assets/images/maithili.png"),
  ml: require("../../../assets/images/malyalm.png"),
  mni: require("../../../assets/images/manipuri.png"),
  mr: require("../../../assets/images/marathi.png"),
  ne: require("../../../assets/images/nepali.png"),
  or: require("../../../assets/images/odia.png"),
  pa: require("../../../assets/images/panjabi.png"),
  sa: require("../../../assets/images/sanskrit.png"),
  sat: require("../../../assets/images/santhali.png"),
  sd: require("../../../assets/images/sindhi.png"),
  ta: require("../../../assets/images/tamil.png"),
  te: require("../../../assets/images/telagu.png"),
  ur: require("../../../assets/images/urdu.png"),
};

export const LANGUAGES = [
  { code: "en", label: "English", isActive: true },
  { code: "hi", label: "Hindi (हिन्दी)", isActive: true },
  { code: "as", label: "Assamese (অসমীয়া)", isActive: true },
  { code: "bn", label: "Bengali (বাংলা)", isActive: true },
  { code: "brx", label: "Bodo (बड़ो)", isActive: true },
  { code: "doi", label: "Dogri (डोगरी)", isActive: true },
  { code: "gom", label: "Goan Konkani (गोवा कोंकणी)", isActive: true },
  { code: "gu", label: "Gujarati (ગુજરાતી)", isActive: true },
  { code: "kn", label: "Kannada (ಕನ್ನಡ)", isActive: true },
  { code: "ks", label: "Kashmiri (کٲشُر)", isActive: true },
  { code: "mai", label: "Maithili (मैथिली)", isActive: true },
  { code: "ml", label: "Malayalam (മലയാളം)", isActive: true },
  { code: "mni", label: "Manipuri (মণিপুরী)", isActive: true },
  { code: "mr", label: "Marathi (मराठी)", isActive: true },
  { code: "ne", label: "Nepali (नेपाली)", isActive: true },
  { code: "or", label: "Odia (ଓଡ଼ିଆ)", isActive: true },
  { code: "pa", label: "Punjabi (ਪੰਜਾਬੀ)", isActive: true },
  { code: "sa", label: "Sanskrit (संस्कृत)", isActive: true },
  { code: "sat", label: "Santali (संताली)", isActive: true },
  { code: "sd", label: "Sindhi (سنڌي)", isActive: true },
  { code: "ta", label: "Tamil (தமிழ்)", isActive: true },
  { code: "te", label: "Telugu (తెలుగు)", isActive: true },
  { code: "ur", label: "Urdu (اردو)", isActive: true },
];

export default function LanguageModal({
  isOpen,
  onClose,
  onLanguageChange,
  expanded,
  anchorRef,
  selectedLanguageCode,
}) {
  const { selectedLanguageData, setSelectedLanguageData } = useAppContext();
  const { setLanguage } = useLocalization();

  const languages = useMemo(() => LANGUAGES.filter((l) => l.isActive), []);
  const activeLanguageCode = selectedLanguageCode || selectedLanguageData?.code;

  const getLocalLabel = (label) => {
    const match = label.match(/\(([^)]+)\)/);
    return match ? match[1] : label.split(" ")[0];
  };

  const getEnglishLabel = (label) => label.replace(/\s*\(.+?\)/, "");

  const select = (lang) => {
    setSelectedLanguageData(lang); // AppContext
    setLanguage(lang.code); // i18n
    onLanguageChange(lang.code);
    window.dispatchEvent(
      new CustomEvent("bhashini-language-select", {
        detail: { languageCode: lang.code },
      }),
    );
    onClose();
  };

  const modalRef = useRef(null);

  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event) => {
      if (
        modalRef.current &&
        !modalRef.current.contains(event.target) &&
        !anchorRef?.current?.contains(event.target)
      ) {
        onClose();
      }
    };

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        event.preventDefault();
        onClose();
        anchorRef?.current?.querySelector?.("button")?.focus?.();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [anchorRef, isOpen, onClose]);

  if (!isOpen) return null;

  if (!expanded) {
    const rect = anchorRef?.current?.getBoundingClientRect();
    const viewportWidth = typeof window === "undefined" ? 360 : window.innerWidth;
    const viewportHeight = typeof window === "undefined" ? 640 : window.innerHeight;
    const dropdownWidth = Math.min(320, viewportWidth - 24);
    const dropdownHeight = Math.min(420, viewportHeight - 24);
    const dropdownLeft = Math.max(
      12,
      Math.min(rect?.left ?? 12, viewportWidth - dropdownWidth - 12),
    );
    const preferredTop = (rect?.top ?? viewportHeight) - dropdownHeight - 8;
    const dropdownTop = Math.max(
      12,
      Math.min(preferredTop, viewportHeight - dropdownHeight - 12),
    );

    return createPortal(
      <LanguageDropdown
        ref={modalRef}
        role="dialog"
        aria-modal="true"
        aria-label="Select language"
        style={{
          top: dropdownTop,
          left: dropdownLeft,
        }}
      >
        {languages.map((l) => {
          const icon = ICONS[l.code];

          return (
            <LanguageItem
              key={l.code}
              type="button"
              selected={l.code === activeLanguageCode}
              aria-pressed={l.code === activeLanguageCode}
              onClick={() => select(l)}
            >
              {icon ? (
                <IconWrapper aria-hidden="true">
                  <img src={icon} alt="" aria-hidden="true" />
                </IconWrapper>
              ) : (
                <IconWrapper aria-hidden="true">{getLocalLabel(l.label)}</IconWrapper>
              )}

              <LanguageText>
                <LocalText>{getLocalLabel(l.label)}</LocalText>

                <EnglishText>{getEnglishLabel(l.label)}</EnglishText>
              </LanguageText>

              {l.code === activeLanguageCode && (
                <CheckWrapper aria-hidden="true">
                  <Check size={16} />
                </CheckWrapper>
              )}
            </LanguageItem>
          );
        })}
      </LanguageDropdown>,
      document.body,
    );
  }

  return createPortal(
    <div
      className="language-modal-overlay"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="language-modal-title"
    >
      <div
        className="language-modal-content"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="language-modal-top">
          <div className="language-modal-title" id="language-modal-title">Select Language</div>
          <button
            type="button"
            onClick={onClose}
            className="close-modal-btn"
            aria-label="Close language selector"
          >
            <X size={18} aria-hidden="true" />
          </button>
        </div>

        <div className="language-grid" role="group" aria-label="Languages">
          {languages.map((l) => {
            const icon = ICONS[l.code];
            return (
              <button
                key={l.code}
                type="button"
                className={`language-card ${l.code === activeLanguageCode ? "selected" : ""}`}
                onClick={() => select(l)}
                aria-pressed={l.code === activeLanguageCode}
              >
                {icon ? (
                  <span className="language-card-icon" aria-hidden="true">
                    <img src={icon} alt="" aria-hidden="true" />
                  </span>
                ) : (
                  <span className="language-card-symbol" aria-hidden="true">
                    {getLocalLabel(l.label)}
                  </span>
                )}
                <span className="language-card-local">
                  {getLocalLabel(l.label)}
                </span>
                <span className="language-card-label">
                  {getEnglishLabel(l.label)}
                </span>
                {l.code === activeLanguageCode && (
                  <span className="language-card-check" aria-hidden="true">
                    <Check size={14} />
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>
    </div>,
    document.body,
  );
}

const LanguageDropdown = styled.div`
  position: fixed;

  width: min(320px, calc(100vw - 24px));
  max-height: min(420px, calc(100dvh - 24px));

  overflow-y: auto;

  background: white;

  border-radius: 20px;

  box-shadow: 0 20px 50px rgba(0, 0, 0, 0.2);

  z-index: 999999999;
`;

const LanguageItem = styled.button`
  width: 100%;
  border: none;
  background: ${({ selected }) => (selected ? "#f5faf9" : "#fff")};

  display: flex;
  align-items: center;

  gap: 12px;

  padding: 12px 16px;
  cursor: pointer;

  &:hover {
    background: #f8f8f8;
  }

  &:focus-visible {
    outline: 2px solid #005856;
    outline-offset: -2px;
  }
`;

const IconWrapper = styled.div`
  width: 42px;
  height: 42px;

  border-radius: 50%;

  display: flex;
  align-items: center;
  justify-content: center;

  background: #f3f4f6;

  img {
    width: 24px;
    height: 24px;
  }
`;

const LanguageText = styled.div`
  flex: 1;
  text-align: left;
`;

const LocalText = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: #1b386b;
`;

const EnglishText = styled.div`
  font-size: 14px;
  color: #666;
`;

const CheckWrapper = styled.div`
  color: #008787;
`;

LanguageModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onLanguageChange: PropTypes.func.isRequired,
  selectedLanguageCode: PropTypes.string,
};
